/**
 * Le Bonus Répar d'un atelier, pour tout ce que le front en dit : la carte du
 * listing, la tête de fiche, les prestations, le tunnel, le filtre et
 * l'introduction d'une ville. L'atelier est dit « labellisé Bonus Répar »
 * (badge) et le barème se dit avec les prix, jamais en petite mention sur une
 * prestation.
 *
 * Il vient de la fiche annuaire, déjà servie avec le rendu serveur
 * (`shop.fixer_lite`, FIXERLIGHT-124) : `bonus_repar` dit si l'atelier
 * l'applique, `bonus_repar_tiers` porte le barème `[{ seuil_cents,
 * montant_cents }]`, et `[]` quand l'atelier ne l'applique pas. Les montants
 * viennent de l'api, jamais d'ici : un barème mal formé ne s'affiche pas, car
 * une remise fausse promise à un client vaut moins que pas de remise du tout.
 *
 * Pur, sans React ni I/O, et défensif comme `bookingState` : une fiche sans
 * `fixer_lite` (aperçu branché sur un staging en retard) rend « rien à dire »,
 * jamais une erreur, puisque la fiche est rendue côté serveur.
 */
import { isFixerLiteEnabled } from './bookingState';

const entier = (n) => Number.isInteger(n) && n > 0;

/**
 * Paliers en euros, du plus bas au plus haut, ou `null` : rien à dire. Un
 * barème incohérent ne s'affiche pas (voir plus bas ce qu'est « cohérent »).
 */
export const paliersBonusRepar = (fixerLite) => {
  if (!fixerLite || fixerLite.bonus_repar !== true) return null;
  const tiers = fixerLite.bonus_repar_tiers;
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  if (
    !tiers.every((t) => t && entier(t.seuil_cents) && entier(t.montant_cents))
  )
    return null;
  const tries = [...tiers].sort((a, b) => a.seuil_cents - b.seuil_cents);
  // Seuils distincts et croissants, remise qui ne baisse pas quand le seuil
  // monte (deux paliers peuvent offrir la même), et jamais une remise qui
  // égale ou dépasse la dépense qu'elle récompense.
  const coherent = tries.every(
    (t, i) =>
      t.montant_cents < t.seuil_cents &&
      (i === 0 ||
        (t.seuil_cents > tries[i - 1].seuil_cents &&
          t.montant_cents >= tries[i - 1].montant_cents)),
  );
  if (!coherent) return null;
  return tries.map((t) => ({
    seuil: t.seuil_cents / 100,
    montant: t.montant_cents / 100,
  }));
};

/**
 * Les paliers d'une fiche qui prend ses rendez-vous en ligne, ou `null`. Une
 * fiche qui n'est pas Fixer Lite n'a rien à dire, quoi que porte son bloc.
 */
export const paliersDeLaFiche = (shop) =>
  isFixerLiteEnabled(shop) ? paliersBonusRepar(shop.fixer_lite) : null;

/** La fiche applique-t-elle le Bonus Répar ? Le filtre et le décompte de ville. */
export const appliqueBonusRepar = (shop) => paliersDeLaFiche(shop) !== null;

// Un montant rond s'écrit sans décimales, les autres avec deux : « 65 € »,
// « 12,50 € ». Espace insécable : « 30 € » ne se coupe pas entre deux lignes.
export const euros = (n) =>
  `${Number.isInteger(n) ? n : n.toFixed(2).replace('.', ',')}\u00a0€`;

/**
 * Le plus haut palier, en une ligne pour la carte du listing : « Jusqu’à 30 €
 * de remise ». La carte compare des ateliers, elle ne détaille pas le barème.
 */
export const remiseMaxBonusRepar = (paliers) =>
  `Jusqu’à ${euros(Math.max(...paliers.map((p) => p.montant)))} de remise`;
